import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link2, Loader2, Plus, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { B } from "../clients/record/BillingCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
const FOCUS = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD108]";

const fig = (m) => `${m.currency} ${Number(m.amount).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Stripe customers that belong to nobody.

   Money first: a customer with a running subscription and no client is revenue
   nobody owns. Three ways out — link to a client, create the client from what
   Stripe already knows, or say it is one of ours and keep it out of revenue. */
export function UnresolvedPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [pick, setPick] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/unmatched`);
      setD(r.data);
    } catch { setD({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, done) => {
    setBusy(id);
    try {
      await fn();
      toast.success(done);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That did not work");
    } finally { setBusy(""); }
  };

  const link = (c) => {
    const clientId = pick[c.id];
    if (!clientId) return;
    const name = (d.clients.find((x) => x.client_id === clientId) || {}).company_name;
    return act(c.id, () => axios.post(`${API}/match`, { customer_id: c.id, client_id: clientId }),
      `${c.name || "Customer"} linked to ${name}`);
  };

  if (!d || d.error) return null;
  const rows = d.customers || [];
  if (rows.length === 0) return null;

  return (
    <div className="rounded-[11px] overflow-hidden" data-testid="billing-unresolved-panel"
      style={{ background: B.card, border: `1px solid ${B.border}` }}>
      <div className="px-3.5 py-2.5 flex items-center gap-2 flex-wrap"
        style={{ borderBottom: `1px solid ${B.divider}` }}>
        <Link2 className="w-4 h-4" style={{ color: B.ink3 }} />
        <span className="text-[13.5px] font-bold" style={{ color: B.ink }}>
          Stripe customers not linked to a client
        </span>
        <span className="text-[12px]" style={{ color: B.ink3 }} data-testid="unresolved-count">
          {rows.length} to resolve{d.with_active_money ? ` · ${d.with_active_money} paying now` : ""}
        </span>
      </div>

      {rows.map((c) => (
        <div key={c.id} className="px-3.5 py-2.5" style={{ borderBottom: `1px solid ${B.divider}` }}
          data-testid={`unresolved-${c.id}`}>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[13px] font-semibold" style={{ color: B.ink }}>
              {c.name || "(no name in Stripe)"}
            </span>
            {c.has_active_money && (
              <span className="text-[10px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded"
                style={{ background: "#FFF8D6", color: "#8A6D00" }}
                data-testid={`unresolved-paying-${c.id}`}>paying</span>
            )}
            <a href={c.dashboard_url} target="_blank" rel="noopener noreferrer"
              className="mono text-[11.5px] underline inline-flex items-center gap-1"
              style={{ color: B.ink3 }}>
              {c.id}<ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: B.ink2 }}>
            {[c.email,
              ...(c.active_subscriptions || []).map(
                (s) => `${(s.products || []).join(" + ") || "Subscription"} ${fig(s.money)}/${s.interval || "period"}`),
              c.paid_total?.amount ? `${fig(c.paid_total)} paid to date` : null,
            ].filter(Boolean).join(" · ") || "No email, no subscription"}
          </div>

          {(c.suggestions || []).length > 0 && (
            <div className="text-[11.5px] mt-1" style={{ color: B.ink3 }}>
              Looks like{" "}
              {c.suggestions.map((s, i) => (
                <button key={s.client_id} disabled={busy === c.id}
                  onClick={() => act(c.id, () => axios.post(`${API}/match`,
                    { customer_id: c.id, client_id: s.client_id }),
                    `${c.name} linked to ${s.company_name}`)}
                  className={`underline font-semibold ${FOCUS}`} style={{ color: B.ink }}
                  data-testid={`unresolved-suggest-${c.id}-${s.client_id}`}>
                  {s.company_name}{i < c.suggestions.length - 1 ? ", " : ""}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <select value={pick[c.id] || ""} disabled={busy === c.id}
              onChange={(e) => setPick((p) => ({ ...p, [c.id]: e.target.value }))}
              className={`text-[12.5px] rounded px-2 py-1 max-w-[260px] ${FOCUS}`}
              style={{ border: `1px solid ${B.border}`, background: B.panel, color: B.ink }}
              data-testid={`unresolved-select-${c.id}`}>
              <option value="">Link to an existing client…</option>
              {(d.clients || []).map((x) => (
                <option key={x.client_id} value={x.client_id}>{x.company_name}</option>
              ))}
            </select>
            <button onClick={() => link(c)} disabled={!pick[c.id] || busy === c.id}
              className={`text-[12.5px] font-semibold px-2.5 py-1 rounded disabled:opacity-40 ${FOCUS}`}
              style={{ background: B.ink, color: "#fff" }}
              data-testid={`unresolved-link-${c.id}`}>
              {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Link"}
            </button>
            <button disabled={busy === c.id || !c.name}
              onClick={() => act(c.id, async () => {
                try {
                  await axios.post(`${API}/customers/create-client`, { customer_id: c.id });
                } catch (e) {
                  const detail = e?.response?.data?.detail || "";
                  // A near-duplicate is refused once; creating anyway is a decision
                  // the person makes with the existing name in front of them.
                  if (e?.response?.status === 409 && detail.includes("confirm to create")) {
                    if (!window.confirm(`${detail}\n\nCreate a separate client anyway?`)) {
                      throw new Error("cancelled");
                    }
                    await axios.post(`${API}/customers/create-client`,
                      { customer_id: c.id, confirm: true });
                  } else { throw e; }
                }
              }, `Client created for ${c.name}`)}
              className={`text-[12.5px] px-2.5 py-1 rounded inline-flex items-center gap-1 disabled:opacity-40 ${FOCUS}`}
              style={{ border: `1px solid ${B.border}`, color: B.ink }}
              data-testid={`unresolved-create-${c.id}`}>
              <Plus className="w-3.5 h-3.5" />Create client
            </button>
            <button disabled={busy === c.id}
              onClick={() => act(c.id, () => axios.post(`${API}/customers/internal`,
                { customer_id: c.id, internal: true }), `${c.name || c.id} marked as ours`)}
              className={`text-[12.5px] px-2.5 py-1 rounded inline-flex items-center gap-1 ml-auto ${FOCUS}`}
              style={{ color: B.ink3 }}
              data-testid={`unresolved-internal-${c.id}`}>
              <EyeOff className="w-3.5 h-3.5" />Ours, not a customer
            </button>
          </div>
        </div>
      ))}
      <div className="px-3.5 py-2 text-[11.5px]" style={{ color: B.ink3 }}>
        Marking one as ours keeps it out of this list and out of revenue. Linking back-fills
        their subscriptions and invoices to that client straight away.
      </div>
    </div>
  );
}
